/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'
import { accessService } from '../services/access.service'
import { getUserRoles } from '../services/auth'
import {
  UserRole,
  UserGroup,
  AccessRule,
  type AccessReference,
  type AsyncAccessOptions,
  type UseAccessControlResult,
  type UseAsyncAccessResult,
  type UseAccessRulesResult,
  type AccessCheckResult
} from '../types/access'

/**
 * React hook for access control functionality
 *
 * Provides convenient methods for checking user access to various system features
 * and components based on roles, permissions, and access rules.
 */
export function useAccessControl(): UseAccessControlResult {
  const [userRoles, setUserRoles] = useState<UserRole[]>([])

  // Update user roles when user changes
  useEffect(() => {
    const updateUserRoles = () => {
      const roles = getUserRoles().filter((role): role is UserRole => role !== null)
      setUserRoles(roles)
    }

    updateUserRoles()

    // Set up listener for user changes (if your auth system supports it)
    // This would depend on your specific auth implementation
    const interval = setInterval(updateUserRoles, 5000) // Check every 5 seconds

    return () => clearInterval(interval)
  }, [])

  /**
   * Check if user is allowed access to a resource
   */
  const isAllowed = useCallback((ref: AccessReference): boolean => {
    return accessService.isAllowed(ref)
  }, [])

  /**
   * Check if user is NOT allowed access to a resource
   */
  const isNotAllowed = useCallback((ref: AccessReference): boolean => {
    return accessService.isNotAllowed(ref)
  }, [])

  /**
   * Get detailed access check result with reasons
   */
  const checkAccess = useCallback((ref: AccessReference): AccessCheckResult => {
    const isAllowedResult = accessService.isAllowed(ref)
    return {
      isAllowed: isAllowedResult,
      reason: isAllowedResult ? 'Access granted' : 'Access denied'
    }
  }, [])

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (role: UserRole): boolean => {
      return userRoles.includes(role)
    },
    [userRoles]
  )

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = useCallback((roles: UserRole[]): boolean => {
    return accessService.areRolesPermitted(roles)
  }, [])

  /**
   * Check if user is in a specific group
   */
  const isInGroup = useCallback(
    (group: UserGroup): boolean => {
      return accessService.checkUserAccess(userRoles, [group])
    },
    [userRoles]
  )

  return {
    isAllowed,
    isNotAllowed,
    checkAccess,
    hasRole,
    hasAnyRole,
    isInGroup
  }
}

/**
 * Hook for async access control operations
 * Handles async permission checks with loading states and error handling
 */
export function useAsyncAccess(): UseAsyncAccessResult {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Async access check with automatic redirect on failure
   */
  const checkAccessAsync = useCallback(
    async (ref: AccessReference, options: AsyncAccessOptions = {}): Promise<boolean> => {
      setIsLoading(true)
      setError(null)

      try {
        const result = await accessService.isAllowedStateAsync(ref, options)
        return result
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Access check failed')
        setError(error)
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  /**
   * Check QC access availability
   */
  const isQCAccessAllowed = useCallback(async (): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      await accessService.isQCAccessAllowedAsync()
      return true
    } catch (err) {
      const error = err instanceof Error ? err : new Error('QC access check failed')
      setError(error)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Check if condition passes
   */
  const checkCondition = useCallback(async (condition: boolean): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      await accessService.passesCondition(condition)
      return true
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Condition check failed')
      setError(error)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    checkAccessAsync,
    isQCAccessAllowed,
    checkCondition,
    isLoading,
    error
  }
}

/**
 * Hook for managing access rules and groups
 * Provides utilities for working with access rule definitions
 */
export function useAccessRules(): UseAccessRulesResult {
  /**
   * Get access rule definition
   */
  const getAccessRule = useCallback((): (UserRole | UserGroup)[] => {
    // This would use the ACCESS_RULES constant from types
    // For now, return empty array as placeholder
    return []
  }, [])

  /**
   * Get all access rules
   */
  const getAllAccessRules = useCallback(() => {
    // This would return the complete ACCESS_RULES mapping
    return {} as Record<AccessRule, (UserRole | UserGroup)[]>
  }, [])

  /**
   * Get group definition (roles that belong to a group)
   */
  const getGroupDefinition = useCallback((): UserRole[] => {
    // This would use the GROUP_DEFINITIONS constant from types
    // For now, return empty array as placeholder
    return []
  }, [])

  /**
   * Expand groups to individual roles
   */
  const expandGroups = useCallback(
    (entities: (UserRole | UserGroup)[]): UserRole[] => {
      const expandedRoles: UserRole[] = []

      entities.forEach((entity) => {
        if (Object.values(UserGroup).includes(entity as UserGroup)) {
          const groupRoles = getGroupDefinition()
          expandedRoles.push(...groupRoles)
        } else {
          expandedRoles.push(entity as UserRole)
        }
      })

      return Array.from(new Set(expandedRoles)) // Remove duplicates
    },
    [getGroupDefinition]
  )

  return {
    getAccessRule,
    getAllAccessRules,
    getGroupDefinition,
    expandGroups
  }
}

/**
 * Hook for role-based component visibility
 * Provides simple boolean flags for showing/hiding UI elements
 */
export function useRoleBasedVisibility() {
  const { hasRole, hasAnyRole, isInGroup } = useAccessControl()

  const visibility = useMemo(
    () => ({
      // Admin visibility
      isAdmin: hasRole(UserRole.ADMIN),
      isContentAdmin: hasRole(UserRole.CONTENT_ADMIN),
      isPowerUser: hasRole(UserRole.POWER_USER),

      // Group visibility
      isRcmAdmin: isInGroup(UserGroup.RCM_ADMINS),
      isRcmUser: isInGroup(UserGroup.RCM_USERS),
      isContentAdminGroup: isInGroup(UserGroup.CONTENT_ADMINS),

      // ETMF visibility
      isEtmfManager: hasRole(UserRole.ETMF_MANAGER),
      isEtmfSpecialist: hasRole(UserRole.ETMF_SPECIALIST),
      isEtmfUser: hasAnyRole([UserRole.ETMF_SITE_USER, UserRole.ETMF_STUDY_USER]),
      isEtmfSponsor: hasRole(UserRole.ETMF_SPONSOR),

      // Other roles
      isAuditor: hasRole(UserRole.AUDITOR),
      isExternal: hasRole(UserRole.EXTERNAL),
      isRcrUser: hasRole(UserRole.RCR_USER)
    }),
    [hasRole, hasAnyRole, isInGroup]
  )

  return visibility
}

/**
 * Hook for protected route access
 * Used for route-level access control
 */
export function useProtectedRoute(requiredAccess: AccessReference) {
  const { isAllowed } = useAccessControl()
  const { checkAccessAsync } = useAsyncAccess()

  const [hasAccess, setHasAccess] = useState<boolean | null>(null)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    const checkRouteAccess = async () => {
      setIsChecking(true)

      try {
        // First try sync check
        const syncResult = isAllowed(requiredAccess)

        if (syncResult) {
          setHasAccess(true)
        } else {
          // If sync check fails, try async check which may redirect
          try {
            await checkAccessAsync(requiredAccess, {
              hideDontAccessErrorMsg: false,
              redirectOnFailure: true
            })
            setHasAccess(true)
          } catch {
            setHasAccess(false)
          }
        }
      } catch (error) {
        console.error('Route access check failed:', error)
        setHasAccess(false)
      } finally {
        setIsChecking(false)
      }
    }

    checkRouteAccess()
  }, [requiredAccess, isAllowed, checkAccessAsync])

  return {
    hasAccess,
    isChecking
  }
}

/**
 * Mutation hook for access-controlled operations
 * Wraps mutations with access checks
 */
export function useAccessControlledMutation<TData = unknown, TError = Error, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  requiredAccess: AccessReference
) {
  const { isAllowed } = useAccessControl()

  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      // Check access before executing mutation
      if (!isAllowed(requiredAccess)) {
        throw new Error('Insufficient permissions to perform this operation')
      }

      return mutationFn(variables)
    },
    onError: (error) => {
      console.error('Access-controlled mutation failed:', error)
    }
  })
}

// Re-export for convenience
export { accessService }
export default useAccessControl
